import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  Home, 
  Image, 
  Map, 
  PlusCircle, 
  Heart, 
  User, 
  LogOut, 
  Menu, 
  X
} from 'lucide-react';

// URL da logo original
const LOGO_URL = "https://customer-assets.emergentagent.com/job_pet-photo-storage/artifacts/343ueuet_logo.png";

const Navbar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { path: '/', label: 'Início', icon: Home },
    { path: '/galeria', label: 'Galeria', icon: Image },
    { path: '/mapa', label: 'Mapa', icon: Map },
    { path: '/ongs', label: 'Parceiros', icon: Heart },
  ];

  const isActive = (path) => location.pathname === path;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#F5D79C]/95 backdrop-blur-sm shadow-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <div className="logo-container p-1 w-14 h-14 overflow-hidden">
              <img 
                src={LOGO_URL} 
                alt="Miaucaomigo Logo" 
                className="w-full h-full object-contain"
              />
            </div>
            <span className="text-xl font-display font-semibold text-[#5D4A3A]">
              Miaucaomigo
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => {
              const Icon = link.icon;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                    isActive(link.path)
                      ? 'bg-[#6B5344] text-white'
                      : 'text-[#5D4A3A] hover:bg-[#FCEBC5]'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="font-medium">{link.label}</span>
                </Link>
              );
            })}
          </div>

          {/* Auth Section */}
          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <>
                <Link to="/cadastrar-pet">
                  <Button className="btn-primary px-6">
                    <PlusCircle className="w-4 h-4 mr-2" />
                    Cadastrar Pet
                  </Button>
                </Link>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="rounded-full border-[#C4956A] bg-[#FCEBC5] text-[#5D4A3A] hover:bg-[#F5E6C8]">
                      <User className="w-4 h-4 mr-2" />
                      {user.nome.split(' ')[0]}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48 bg-[#FCEBC5] border-[#C4956A]">
                    <DropdownMenuItem asChild>
                      <Link to="/meus-pets" className="flex items-center gap-2 cursor-pointer text-[#5D4A3A]">
                        <span>🐾</span>
                        Meus Pets
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-[#C4956A]" />
                    <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer">
                      <LogOut className="w-4 h-4 mr-2" />
                      Sair
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <Link to="/login">
                <Button className="btn-primary px-6">
                  <User className="w-4 h-4 mr-2" />
                  Entrar
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-[#FCEBC5] text-[#5D4A3A]"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-[#FCEBC5] border-t border-[#C4956A] shadow-lg">
          <div className="px-4 py-4 space-y-2">
            {navLinks.map((link) => {
              const Icon = link.icon;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-2xl transition-all ${
                    isActive(link.path)
                      ? 'bg-[#6B5344] text-white'
                      : 'text-[#5D4A3A] hover:bg-[#F5E6C8]'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{link.label}</span>
                </Link>
              );
            })}
            
            <div className="pt-2 border-t border-[#C4956A]">
              {user ? (
                <>
                  <Link
                    to="/cadastrar-pet"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-2xl text-[#5D4A3A] hover:bg-[#F5E6C8]"
                  >
                    <PlusCircle className="w-5 h-5" />
                    <span className="font-medium">Cadastrar Pet</span>
                  </Link>
                  <Link
                    to="/meus-pets"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 rounded-2xl text-[#5D4A3A] hover:bg-[#F5E6C8]"
                  >
                    <span>🐾</span>
                    <span className="font-medium">Meus Pets</span>
                  </Link>
                  <button
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    className="flex items-center gap-3 px-4 py-3 rounded-2xl text-red-600 hover:bg-red-50 w-full"
                  >
                    <LogOut className="w-5 h-5" />
                    <span className="font-medium">Sair</span>
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-[#6B5344] text-white"
                >
                  <User className="w-5 h-5" />
                  <span className="font-medium">Entrar</span>
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
